import Url from 'url-parse';
import { xml2js, Element, ElementCompact } from 'xml-js';
import RS from '~/api';
import { SitemapInfo } from '~/api/data/SitemapInfo';
import { getFromLocalStorage, saveToLocalStorage } from '~/helpers/storage';
import { clickstreamLogger } from '~/logging';

const SITEMAP_STORAGE = '__sitemap__info__';

class SiteXMLService {
  /**
   * Является ли файл xml sitemapindex.
   *
   * @param {ElementCompact | Element} parsedXML
   *
   * @return
   */
  private static isXDocSitemapIndex({ elements }: Element | ElementCompact): boolean {
    return elements.find((e: Element | ElementCompact) => e.type === 'element').name === 'sitemapindex';
  }

  /**
   * Извлечение списка url из sitemapindex
   *
   * @param {string} xmlString
   *
   * @return {string[]}
   */
  private static getDocumentsUrls({ elements }: Element | ElementCompact) {
    return elements
      .find((e: Element | ElementCompact) => e.type === 'element' && e.name === 'sitemapindex')
      .elements
      .filter((e: Element | ElementCompact) => e.type === 'element' && e.name === 'sitemap')
      .map((e: Element | ElementCompact) => {
        return e.elements
          .find((e: Element | ElementCompact) => e.type === 'element' && e.name === 'loc')
          .elements
          .find((e: Element | ElementCompact) => e.type === 'text')
          .text;
      });
  }

  /**
   * Содержит ли строка xml в себе.
   *
   * @param {string} xmlString  Xml-файл.
   *
   * @return {boolean}
   */
  private static validateXML(xmlString: string) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    return xmlDoc.getElementsByTagName('parsererror').length <= 0;
  }

  /**
   * Возвращает первый елемент paylod запроса с проверкой
   * @param payload
   * @private
   */
  private static getPayload(payload: SitemapInfo[]) {
    if (payload && payload.length) {
      return payload[0];
    }

    return null;
  }


  /**
   * Является ли дата сегодняшним днем.
   *
   * @param {Date} inputDate
   *
   * @return {boolean}
   */
  private static isInToday(inputDate: Date) {
    const today = new Date();

    return today.setHours(0, 0, 0, 0) === inputDate.setHours(0, 0, 0, 0);
  }

  /**
   * Проверяем валидна ли текущая информация о siteMap.
   *
   * @return {boolean}
   */
  private static isSiteMapInfoAvailableLocalCheck() {
    const currentSiteMapDate = getFromLocalStorage(
      SITEMAP_STORAGE,
      'timestamp'
    );

    return currentSiteMapDate && SiteXMLService.isInToday(new Date(currentSiteMapDate));
  }

  /**
   * Рекурсивная функция обхода, если сайт имеет несколько xml файлов.
   * @param {string} url Адрес по которому запрашиваем xml
   *
   * @return {Promise}
   */
  private async getSiteMapList(url: string) {
    const result: string[] = [];
    const response = await fetch(url);
    const xmlString = await response.text();

    if (!SiteXMLService.validateXML(xmlString)) {
      return result;
    }
    const parsedXML = xml2js(xmlString);
    const isXDocSitemapIndex = SiteXMLService.isXDocSitemapIndex(parsedXML);

    if (isXDocSitemapIndex) {
      const documentsUrls = SiteXMLService.getDocumentsUrls(parsedXML);
      for (let i = 0; i < documentsUrls.length; i++) {
        const urlObject = new Url(documentsUrls[i]);
        const sitemapUrl = urlObject.set('host', location.host, null).toString();
        const iterationResult = await this.getSiteMapList(sitemapUrl);
        result.push(...iterationResult);
      }
    } else {
      result.push(xmlString);
    }

    return result;
  }

  /**
   * Промис который пробрасывает дальше по цепочке массив с xml файлами для отправки на бэк
   *
   * @param sitemapId
   */
  private async getSiteMap(sitemapId: number | string) {
    const siteMapUrl = `${location.origin}/sitemap.xml`;
    const siteMapXMLsArray = await this.getSiteMapList(siteMapUrl);

    if (siteMapXMLsArray.length) {
      return Promise.resolve({ siteMapXMLsArray, sitemapId, siteMapUrl });
    }

    return Promise.reject(`Не удалось получить список валидных xml файлов.`);
  }

  start() {
    // Если информация в LocalStorage валидна, то запрос на сервер не делаем.
    if (SiteXMLService.isSiteMapInfoAvailableLocalCheck()) return;
    let sitemapId: number;
    RS.siteMap.get()
      .then(({ payload }) => {
        const sitemapInfo = SiteXMLService.getPayload(payload);
        if (!sitemapInfo.isSitemapValid) {
          return this.getSiteMap(sitemapInfo.sitemapId)
            .then(RS.siteMap.set)
            .then(({ payload }) => {
              const sitemapInfo = SiteXMLService.getPayload(payload);
              if (sitemapInfo.isSitemapValid)
                sitemapId = sitemapInfo.sitemapId;
            });
        } else {
          sitemapId = sitemapInfo.sitemapId;

          return Promise.resolve();
        }
      })
      .catch((e) => {
        clickstreamLogger.error(['Ошибка инициализации сервиса SitemapXMLService', e]);
      })
      .finally(() => {
        saveToLocalStorage(SITEMAP_STORAGE, 'timestamp', Date.now());
        if (typeof sitemapId !== 'undefined')
          saveToLocalStorage(SITEMAP_STORAGE, 'sitemapId', sitemapId);
      });
  }
}

export default SiteXMLService;
